import React, { useRef, useState, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import Label from '../Form/Label';
import Colors from 'constants/Colors';
import { FiraSansBold, FontWeight } from '../Text';
import ValidationError from '../Form/ValidationError';

import './styles.css';
import { ValidationErrorText, MidDiv, InnerDiv, Button } from './styles';
import Icon from 'components/Icon';

export default function TextEditorBox(props) {
  let {
    type,
    placeholder,
    disabled,
    readOnly,
    mask,
    leftIcon,
    leftIconClick,
    leftIconText,
    rightIcon,
    rightIconClick,
    leftRender,
    rightRender,
    isLoading,
    label,
    field,
    format,
    parse,
    size,
    setLabelMinHeight,
    fieldProps,
    isRequired,
    tabIndex,
    form: { setFieldValue, setFieldTouched, touched, errors, setFieldError },
    value,
    validationMinHeight,
    hideLabelAsterisk,
    smallInput,
    alignCenter,
    inline,
    validation,
    inverseBlue,
    onInputRef,
    onChange,
    onBlur,
    onFocus,
    onPressEnter,
    valueCalendar,
    originalSize,
    customValue,
    onSearchSelect,
    extraLabelTop,
    key,
    filterField,
    infoContent,
    warningByHiddenQuestions,
    refusedByHiddenQuestions,
    preview = true
  } = props;

  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const firstChange = useRef(false);
  const [loadPreview, setLoadPreview] = useState(preview);
  const [loadPreviewUnmount, setLoadPreviewUnmount] = useState(false);

  const handleChange = () => {
    if (editorRef.current && props.onChange) {
      if (meta.error) setFieldError(field.name, '')

      if (!firstChange.current) return firstChange.current = true;

      props.onChange(editorRef.current.getContent());
    }
  };

  var touchedList;
  var touchedItem;
  var errorList;
  var errorItem;

  const meta = {
    touched: touched[field.name],
    error: errors[field.name]
  };

  const _onFocus = () => {
    setIsFocused(true);
  };

  const _onBlur = () => {
    setIsFocused(false);
    setLoadPreviewUnmount(true);
  };

  useEffect(() => {
    setTimeout(() => {
      if (loadPreviewUnmount) {
        setLoadPreview(true);
        setLoadPreviewUnmount(false);
      }
    }, 100);
  }, [loadPreviewUnmount]);

  useEffect(() => {
    setTimeout(() => !firstChange.current && !disabled ? firstChange.current = true : false, 500)
  }, []);

  useEffect(() => {
    if (editorRef.current && !disabled) {
        editorRef.current.getBody().style.backgroundColor = Colors.WHITE;
        editorRef.current.getBody().style.color = Colors.BLACK;
    } 
    
    if (editorRef.current && disabled) {
      editorRef.current.getBody().style.backgroundColor = Colors.GRAY[4];
      editorRef.current.getBody().style.color = Colors.GRAY[0];
    }

  }, [disabled]);

  return (
    <div style={{ marginBottom: 10 }}>
      <Label
        meta={meta}
        width={props.inline ? 35 : null}
        isRequired={props.isRequired}
        weight={FontWeight.SemiBold}
        setMinHeight={props.setLabelMinHeight}
        inline={props.inline}
        inverse={props.inverseBlue}
        hideLabelAsterisk={props.hideLabelAsterisk}
        infoContent={props.infoContent}
      >{props.label || ''}</Label>
      <div style={{
        minHeight: props.height || 196,
        background: 'rgba(55,55,55,0.05)',
        borderRadius: 6,
        ...(meta?.error ? { boxShadow: `0 0 0 3px ${Colors.DANGER}` } : {})
      }}>
        {
          loadPreview ? 
            <MidDiv height={props.height}>
              <InnerDiv
                dangerouslySetInnerHTML={{ __html: field.value || props.initialValue }}
                errors={errors}
              />
              {
                (!disabled && !readOnly) &&
                  <Button
                    onClick={() => {
                      setLoadPreview(false);
                    }}
                  >
                    <Icon name="edit"/> 
                  </Button>
              }
            </MidDiv> :
            <Editor
              apiKey={process.env.REACT_APP_TINYMCE_TOKEN}
              onInit={(evt, editor) => {
                editorRef.current = editor

                if (disabled) {
                  editor.getBody().style.backgroundColor = Colors.GRAY[4];
                  editor.getBody().style.color = Colors.GRAY[0];
                }
              }}
              value={value}
              onEditorChange={handleChange}
              disabled={disabled}
              onFocus={() => {
                _onFocus();
                onFocus && onFocus(field.value);
              }}
              onBlur={() => {
                onBlur && onBlur(field.value);
                _onBlur();
                if (!meta.touched)
                  setFieldTouched(field.name, true)
              }}
              init={{
                content_style: "body {font-size: 14pt;}",
                inline_styles: true,
                height: props.height,
                menubar: false,
                branding: false,
                relative_urls: false,
                remove_script_host: false,
                entity_encoding : "raw",
                plugins: [
                  'advlist autolink lists link image charmap print preview anchor',
                  'searchreplace visualblocks code fullscreen',
                  'insertdatetime media table paste code help wordcount'
                ],
                toolbar: !props?.disableFullScreen
                  ? 'undo redo | bold italic underline strikethrough | fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat table| fullscreen | setEditorInitialValue '
                  : 'undo redo | bold italic underline strikethrough | fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat table| setEditorInitialValue ',
                setup: (editor) => {
                  editor.ui.registry.addIcon('editor-initial-value', '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#353535" viewBox="0 0 256 256"><path d="M213.66,82.34l-56-56A8,8,0,0,0,152,24H56A16,16,0,0,0,40,40V216a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V88A8,8,0,0,0,213.66,82.34ZM160,176H96a8,8,0,0,1,0-16h64a8,8,0,0,1,0,16Zm0-32H96a8,8,0,0,1,0-16h64a8,8,0,0,1,0,16Zm-8-56V44l44,44Z"></path></svg>');
                  editor.ui.registry.addButton('setEditorInitialValue', {
                    text: 'Atualizar com texto padrão',
                    tooltip: 'Atualizar com texto padrão',
                    icon: 'editor-initial-value',
                    onAction: () => props.initialValue ? editor.setContent(props.initialValue) : editor.resetContent()
                  });
                }
              }}
            />
        }
        {validation && (
          <ValidationError
            style={{ minWidth: '100%', position: 'absolute', bottom: -30, paddingRight: 20 }}
            meta={meta}
            validationMinHeight={validationMinHeight}
          />
        )}
      </div>
    </div>
  );
}
